/**
 * /pressure-testing/certificates/:certificateId — one certificate's review on
 * its own page (PT-4, fieldiq-api#921 `GET /certificates/:id`). A row on the
 * certificate register or a test job's certificate card opens it here. It was
 * a slide-over drawer until drawers and split panes beside a list were banned
 * (CLAUDE.md); the back arrow returns to the register with its status chip and
 * search as they were left, or to the job that opened it.
 *
 * WHAT IT SHOWS IS THE API'S. Kind, flags, number, signatories and every
 * pressure come from `GET /certificates/:id` as returned; nothing here decides
 * pass or failure, counts flags into a verdict, or composes a number. The
 * "paper" is a reading of the certificate data in the PDF's order — letterhead,
 * PASS/FAILURE banner, job grid, tests, chart photos, calibration reference,
 * three signature blocks — and the PDF itself is one click away (Preview PDF),
 * because only the API's render is the certificate.
 *
 * WHAT THE API DOES NOT RETURN, and so is not drawn:
 *  - SIGNATURE IMAGES. A signature is an R2 key the API reads only to render
 *    the PDF; the read carries signer, time and present/missing. Preview PDF
 *    shows the strokes.
 *  - EMAIL HISTORY. Each send is a pt_certificate_emails row, but no read
 *    returns those rows. The only result this screen sees is the issue's own
 *    `email`, shown for the session it happened in; otherwise the section says
 *    earlier sends are not listed rather than reading as "never emailed".
 *
 * ACTIONS, by status, for PT_DISPATCH roles (the API's requireRole; a button it
 * would refuse is left off):
 *   draft     Preview PDF · Rebuild draft · Reject · Issue
 *   rejected  Preview PDF · Rebuild draft   (issue answers certificate_rejected)
 *   issued    Download PDF                  (immutable in the database)
 * They sit on the band, top right. Every refusal is the API's sentence, shown
 * on the page.
 */
import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Bot,
  Download,
  FileText,
  Info,
  Loader2,
  Mail,
  PenLine,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, getBlobApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import {
  useDraftPtCertificate,
  useIssuePtCertificate,
  usePtCertificate,
  usePtJob,
  useRejectPtCertificate,
} from '../../hooks/usePressureTesting';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, saveBlobAs, showBlobInTab } from '../../lib/blob-tab';
import { formatCalendarDate, formatInstant, formatInstantTenantDay } from '../../lib/calendar-date';
import { isNotFound, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canDispatchPt,
  formatPressure,
  PT_CERT_FORMAT_LABEL,
  ptCertKindSkin,
  ptCertStatusSkin,
  ptFlagSourceLabel,
  ptResultSkin,
} from '../../lib/pressure-testing';
import type {
  PtCertificate,
  PtCertificateEmailResult,
  PtCertificateFlag,
  PtPressureUnit,
  PtSignature,
  PtTest,
} from '../../types/pressure-testing';
import { PT_BAND_SECONDARY, PT_INPUT, PT_SECONDARY, PtDialog, PtField, PtSkinBadge } from './PtBits';
import { PtChartImage } from './PtChartPhoto';
import { ptWriteError } from './PtJobDialogs';
import { useBackToList } from '../downhole/recordPage';

const LIST = '/pressure-testing/certificates';

const time = (iso: string | null | undefined, tz: string | null) =>
  formatInstant(iso, tz, { hour: 'numeric', minute: '2-digit', hour12: true });

/**
 * One row of the paper's info grid. The label column is a FIXED width and the
 * value is right-aligned and wraps inside its own column, so a long customer
 * name or scope can never run into its label (the mockup's rule).
 */
function PaperRow({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] items-baseline gap-3 border-b border-fieldiq-border/60 py-1.5 last:border-b-0">
      <dt className="text-[0.62rem] font-black uppercase tracking-[0.1em] text-fieldiq-muted">{label}</dt>
      <dd className={`break-words text-right text-sm text-white ${mono ? 'font-mono tabular-nums' : ''}`}>{value}</dd>
    </div>
  );
}

function PaperHeading({ children }: { children: React.ReactNode }) {
  return <h4 className="mb-2 mt-5 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{children}</h4>;
}

/** A signature block: present (who, when) or missing — the API returns no image. */
function SignatureBlock({ title, signature, tz }: { title: string; signature: PtSignature | null; tz: string | null }) {
  return (
    <div
      data-testid={`pt-cert-signature-${title.toLowerCase().replace(/\s+/g, '-')}`}
      data-present={signature ? 'true' : 'false'}
      className="rounded-fi-control border border-fieldiq-border px-3 py-3"
    >
      <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{title}</p>
      {signature ? (
        <>
          <p className="mt-2 border-b border-fieldiq-border pb-1 text-sm font-semibold text-white">{signature.signer_name || '—'}</p>
          <p className="mt-1 text-xs text-fieldiq-muted">Signed {formatInstant(signature.signed_at, tz) ?? signature.signed_at_local ?? '—'}</p>
        </>
      ) : (
        <>
          <p className="mt-2 border-b border-dashed border-fieldiq-border pb-1 text-sm text-fieldiq-text">Not signed</p>
          <p className="mt-1 text-xs text-fieldiq-muted">Missing</p>
        </>
      )}
    </div>
  );
}

/** The certificate as a document, in the PDF's order. */
function CertificatePaper({
  cert,
  unit,
  tz,
  jobTests,
}: {
  cert: PtCertificate;
  unit: PtPressureUnit | null;
  tz: string | null;
  jobTests: Map<string, PtTest>;
}) {
  const job = cert.job;
  const kind = ptCertKindSkin(cert.kind);
  const failure = cert.kind === 'failure';
  const charted = cert.tests.filter((t) => t.has_chart);
  const formatLabel = PT_CERT_FORMAT_LABEL[cert.cert_format as string] ?? null;

  return (
    <article
      data-testid="pt-cert-paper"
      className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-5 py-5 shadow-sm"
    >
      {/* Letterhead */}
      <header className="flex flex-wrap items-start justify-between gap-3 border-b-2 border-fieldiq-border pb-3">
        <div>
          <p className="text-[0.62rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
            Pressure test certificate{formatLabel ? ` · ${formatLabel}` : ''}
          </p>
          <p className="mt-1 font-mono text-lg font-extrabold text-white" data-testid="pt-cert-number">
            {cert.certificate_number || 'Draft — not issued'}
          </p>
        </div>
        <div className="text-right text-xs text-fieldiq-muted">
          <p>Job <span className="font-mono font-bold text-fieldiq-text">{job.job_number || '—'}</span></p>
          <p>Issued {cert.issued_at ? formatInstantTenantDay(cert.issued_at, tz) ?? '—' : '—'}</p>
        </div>
      </header>

      {/* PASS / FAILURE banner — the API's kind, not a reading of the tests */}
      <div
        data-testid="pt-cert-kind-banner"
        data-kind={cert.kind ?? ''}
        className={`mt-4 rounded-fi-control border border-black/20 px-4 py-3 text-center text-base font-black uppercase tracking-[0.2em] text-fi-signal-ink ${kind.fill}`}
      >
        {failure ? 'Failure' : kind.label === '—' ? '—' : 'Pass'}
      </div>

      <div className="mt-4 grid gap-x-6 sm:grid-cols-2">
        <dl>
          <PaperRow label="Customer" value={job.customer_name || '—'} />
          <PaperRow label="Contact" value={job.customer_contact_name || '—'} />
          <PaperRow label="PO" value={job.po_number || '—'} mono />
          <PaperRow label="AFE" value={job.afe_number || '—'} mono />
        </dl>
        <dl>
          <PaperRow label="Location" value={job.location_name || '—'} />
          <PaperRow label="LSD" value={job.lsd || '—'} mono />
          <PaperRow label="UWI" value={job.uwi || '—'} mono />
          <PaperRow label="Province / state" value={job.province_state || '—'} />
          <PaperRow label="Technician" value={job.technician_name || '—'} />
          <PaperRow label="Completed" value={formatInstant(job.completed_at, tz) ?? '—'} />
        </dl>
      </div>
      <dl className="mt-1">
        <PaperRow label="Scope" value={<span className="whitespace-pre-wrap">{job.scope || '—'}</span>} />
      </dl>

      <PaperHeading>Tests</PaperHeading>
      {cert.tests.length === 0 ? (
        <p className="text-sm text-fieldiq-text">No tests are recorded on this job.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm" data-testid="pt-cert-tests">
            <thead className="border-b border-fieldiq-border text-[0.6rem] font-black uppercase tracking-[0.1em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="py-2 pr-2">#</th>
                <th scope="col" className="py-2 pr-2">Test</th>
                <th scope="col" className="py-2 pr-2 text-right">Test pressure</th>
                <th scope="col" className="py-2 pr-2 text-right">Start / end</th>
                <th scope="col" className="py-2 pr-2 text-right">Drop / allowable</th>
                <th scope="col" className="py-2 pr-2 text-right">Hold</th>
                <th scope="col" className="py-2">Result</th>
              </tr>
            </thead>
            <tbody>
              {cert.tests.map((t) => (
                <tr key={t.id} className="border-b border-fieldiq-border/60 align-top last:border-b-0" data-testid="pt-cert-test-row">
                  <td className="py-2 pr-2 font-mono text-fieldiq-muted">{t.number}</td>
                  <td className="py-2 pr-2 font-semibold text-white">{t.test_type_name || '—'}</td>
                  <td className="whitespace-nowrap py-2 pr-2 text-right font-mono tabular-nums text-fieldiq-text">{formatPressure(t.test_pressure, unit)}</td>
                  <td className="whitespace-nowrap py-2 pr-2 text-right font-mono tabular-nums text-fieldiq-text">
                    {formatPressure(t.start_pressure, null)} / {formatPressure(t.end_pressure, unit)}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-2 text-right font-mono tabular-nums text-fieldiq-text">
                    {formatPressure(t.pressure_drop, null)} / {formatPressure(t.allowable_drop, unit)}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-2 text-right font-mono tabular-nums text-fieldiq-text">
                    <p>{t.hold_minutes == null ? '—' : `${t.hold_minutes} min`}</p>
                    <p className="text-xs text-fieldiq-muted">
                      {t.hold_started_at ? `${time(t.hold_started_at, tz) ?? '—'} – ${time(t.hold_ended_at, tz) ?? '…'}` : 'Not recorded'}
                    </p>
                  </td>
                  <td className="py-2"><PtSkinBadge skin={ptResultSkin(t.result)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {cert.tests.some((t) => t.notes) && (
        <ul className="mt-2 space-y-1 text-xs text-fieldiq-text">
          {cert.tests.filter((t) => t.notes).map((t) => (
            <li key={t.id}>
              <span className="font-bold">Test {t.number}:</span> {t.notes}
            </li>
          ))}
        </ul>
      )}

      <PaperHeading>Chart photos</PaperHeading>
      {charted.length === 0 ? (
        <p className="text-sm text-fieldiq-text" data-testid="pt-cert-no-charts">No chart photos are attached to these tests.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="pt-cert-charts">
          {charted.map((t) => {
            const jt = jobTests.get(t.id);
            return (
              <figure key={t.id} className="rounded-fi-control border border-fieldiq-border p-2">
                <figcaption className="mb-1 text-xs font-bold text-fieldiq-text">
                  Test {t.number} · {t.test_type_name || '—'}
                </figcaption>
                {jt ? (
                  <PtChartImage test={jt} timeZone={tz} />
                ) : (
                  <p className="text-sm text-fieldiq-text">A chart photo is attached, but it could not be loaded here.</p>
                )}
              </figure>
            );
          })}
        </div>
      )}

      <PaperHeading>Calibration reference</PaperHeading>
      {cert.calibration_reference.length === 0 ? (
        <p className="text-sm text-fieldiq-text">No instrument calibrations are cited.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] text-left text-sm" data-testid="pt-cert-calibrations">
            <thead className="border-b border-fieldiq-border text-[0.6rem] font-black uppercase tracking-[0.1em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="py-2 pr-2">Instrument</th>
                <th scope="col" className="py-2 pr-2">Serial</th>
                <th scope="col" className="py-2 pr-2">Cal. certificate</th>
                <th scope="col" className="py-2 pr-2">Calibrated</th>
                <th scope="col" className="py-2 pr-2">Due</th>
                <th scope="col" className="py-2">Tests</th>
              </tr>
            </thead>
            <tbody>
              {cert.calibration_reference.map((c) => (
                <tr key={c.calibration_id} className="border-b border-fieldiq-border/60 last:border-b-0">
                  <td className="py-2 pr-2 text-white">
                    {c.name || '—'}
                    <span className="ml-1 text-xs text-fieldiq-muted">({c.role === 'recorder' ? 'recorder' : c.role === 'gauge' ? 'gauge' : '—'})</span>
                  </td>
                  <td className="py-2 pr-2 font-mono text-fieldiq-text">{c.serial_number || '—'}</td>
                  <td className="py-2 pr-2 font-mono text-fieldiq-text">{c.certificate_number || '—'}</td>
                  <td className="whitespace-nowrap py-2 pr-2 text-fieldiq-text">{formatCalendarDate(c.calibrated_on) ?? '—'}</td>
                  <td className="whitespace-nowrap py-2 pr-2 text-fieldiq-text">{formatCalendarDate(c.due_on) ?? '—'}</td>
                  <td className="py-2 font-mono text-fieldiq-text">{c.tests.length ? c.tests.join(', ') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PaperHeading>Signatures</PaperHeading>
      <div className="grid gap-3 sm:grid-cols-3">
        <SignatureBlock title="Technician" signature={cert.signatures.technician} tz={tz} />
        <SignatureBlock title="Witness" signature={cert.signatures.witness} tz={tz} />
        <div
          data-testid="pt-cert-signature-issued-by"
          data-present={cert.issued_signatory_name ? 'true' : 'false'}
          className="rounded-fi-control border border-fieldiq-border px-3 py-3"
        >
          <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Issued by</p>
          {cert.issued_signatory_name ? (
            <>
              <p className="mt-2 border-b border-fieldiq-border pb-1 text-sm font-semibold text-white">{cert.issued_signatory_name}</p>
              <p className="mt-1 text-xs text-fieldiq-muted">Issued {formatInstant(cert.issued_at, tz) ?? cert.issued_at_local ?? '—'}</p>
            </>
          ) : (
            <>
              <p className="mt-2 border-b border-dashed border-fieldiq-border pb-1 text-sm text-fieldiq-text">Not issued</p>
              <p className="mt-1 text-xs text-fieldiq-muted">Signed by whoever issues it</p>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

function FlagList({ flags }: { flags: PtCertificateFlag[] }) {
  if (flags.length === 0) {
    return <p className="text-sm text-fieldiq-text" data-testid="pt-cert-no-flags">No review flags.</p>;
  }
  return (
    <ul className="space-y-2" data-testid="pt-cert-flags">
      {flags.map((f, i) => (
        <li
          key={`${f.code}-${f.test_id ?? 'job'}-${i}`}
          data-code={f.code}
          data-source={f.source}
          className={`flex gap-2 rounded-fi-control border border-fieldiq-border border-l-4 bg-fieldiq-dark/40 px-3 py-2 text-sm text-fieldiq-text ${
            f.severity === 'warning' ? 'border-l-fi-amber' : 'border-l-fi-steel'
          }`}
        >
          {f.severity === 'warning' ? (
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fieldiq-muted" aria-label="Warning" />
          ) : (
            <Info className="mt-0.5 size-4 shrink-0 text-fieldiq-muted" aria-label="Note" />
          )}
          <span className="min-w-0 flex-1">{f.message || '—'}</span>
          <span className="shrink-0 self-start rounded-fi-pill border border-fieldiq-border px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.06em] text-fieldiq-muted">
            {ptFlagSourceLabel(f.source)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function RecordSection({ title, icon, children, testId }: { title: string; icon?: React.ReactNode; children: React.ReactNode; testId?: string }) {
  return (
    <section data-testid={testId} className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-extrabold text-white">
        {icon}
        {title}
      </h3>
      {children}
    </section>
  );
}

/** Reject: a reason is required, and it goes to the technician. */
function RejectDialog({ open, onClose, cert }: { open: boolean; onClose: () => void; cert: PtCertificate }) {
  const reject = useRejectPtCertificate();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = () => {
    if (!cert.id) return;
    if (!reason.trim()) {
      setError('A reason is required to reject a certificate.');
      return;
    }
    setError(null);
    reject.mutate(
      { id: cert.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`${cert.job.job_number} certificate rejected`);
          onClose();
        },
        onError: (err) => setError(ptWriteError(err, 'The certificate was not rejected.').message),
      },
    );
  };
  return (
    <PtDialog
      open={open}
      title="Reject certificate"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="fi-action fi-action--standard fi-action--primary"
            onClick={submit}
            disabled={reject.isPending}
            data-testid="pt-cert-reject-confirm"
          >
            {reject.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Reject
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-fieldiq-text">
          The draft goes back to {cert.job.technician_name || 'the technician'} with your reason. To issue it later, rebuild the draft.
        </p>
        <PtField label="Reason">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={2000}
            className={`${PT_INPUT} h-auto py-2`}
            data-testid="pt-cert-reject-reason"
            placeholder="What has to be fixed before this can be issued"
          />
        </PtField>
        {error && (
          <p role="alert" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
            {error}
          </p>
        )}
      </div>
    </PtDialog>
  );
}

/** Issue: the confirm, with the email toggle and the recipient it would go to. */
function IssueDialog({
  open,
  onClose,
  cert,
  onIssued,
}: {
  open: boolean;
  onClose: () => void;
  cert: PtCertificate;
  onIssued: (result: PtCertificate | null) => void;
}) {
  const issue = useIssuePtCertificate();
  const recipient = cert.job.customer_contact_email;
  // Off until the person turns it on: an issue that emails a customer is the
  // one write here that leaves the building.
  const [email, setEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const flagCount = cert.flags.length;

  const submit = () => {
    if (!cert.id) return;
    setError(null);
    issue.mutate(
      { id: cert.id, email: email && !!recipient },
      {
        onSuccess: (res) => {
          onIssued(res.data);
          onClose();
        },
        onError: (err) => setError(ptWriteError(err, 'The certificate was not issued.').message),
      },
    );
  };

  return (
    <PtDialog
      open={open}
      title="Issue certificate"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="fi-action fi-action--standard fi-action--primary"
            onClick={submit}
            disabled={issue.isPending}
            data-testid="pt-cert-issue-confirm"
          >
            {issue.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <ShieldCheck className="size-4" aria-hidden="true" />}
            Issue
          </button>
        </>
      }
    >
      <div className="space-y-4 text-sm text-fieldiq-text">
        <p>
          Issuing {cert.job.job_number}'s certificate assigns the next certificate number, signs it in your name and stores the PDF.{' '}
          <span className="font-bold">An issued certificate cannot be changed or issued again.</span>
        </p>
        {flagCount > 0 && (
          <p className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-dark/40 px-3 py-2" data-testid="pt-cert-issue-flags">
            {flagCount} review flag{flagCount === 1 ? ' is' : 's are'} on this certificate. Flags are advisory and do not stop an issue.
          </p>
        )}
        <label className={`flex items-start gap-3 rounded-fi-control border border-fieldiq-border px-3 py-3 ${recipient ? 'cursor-pointer' : 'opacity-70'}`}>
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-fieldiq-accent"
            checked={email && !!recipient}
            disabled={!recipient}
            onChange={(e) => setEmail(e.target.checked)}
            data-testid="pt-cert-issue-email"
          />
          <span>
            <span className="block font-bold text-white">Email to customer contact</span>
            {recipient ? (
              <span className="block text-fieldiq-text" data-testid="pt-cert-issue-recipient">
                {cert.job.customer_contact_name ? `${cert.job.customer_contact_name} · ` : ''}
                {recipient}
              </span>
            ) : (
              <span className="block text-fieldiq-text" data-testid="pt-cert-issue-no-recipient">
                This job's customer contact has no email address, so the certificate can only be issued without email.
              </span>
            )}
          </span>
        </label>
        {error && (
          <p role="alert" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2">
            {error}
          </p>
        )}
      </div>
    </PtDialog>
  );
}

export default function CertificateDetailScreen() {
  const { certificateId } = useParams<{ certificateId: string }>();
  const back = useBackToList(LIST);
  const { user } = useAuth();
  const query = usePtCertificate(certificateId);
  const cert = query.data?.data ?? null;
  const meta = query.data?.meta;
  const unit = meta?.pressure_unit ?? null;
  const tz = meta?.time_zone ?? null;
  const jobQuery = usePtJob(cert?.job_id);
  const rebuild = useDraftPtCertificate();
  const [dialog, setDialog] = useState<'issue' | 'reject' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [emailResult, setEmailResult] = useState<PtCertificateEmailResult | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const failure = queryLoadFailure(query);
  // A 404 is an answer about the id, not about the network.
  const notFound = !cert && !query.isLoading && (isNotFound(queryFailureError(query)) || !failure);

  const jobTests = useMemo(() => new Map((jobQuery.data?.data?.tests ?? []).map((t) => [t.id, t])), [jobQuery.data]);
  const canAct = canDispatchPt(user?.role);
  const status = cert?.status ?? null;

  const openPdf = async (mode: 'preview' | 'download') => {
    if (!cert?.id) return;
    setActionError(null);
    // Claimed synchronously, inside the click's activation window (lib/blob-tab.ts).
    const win = mode === 'preview' ? claimBlobTab() : null;
    if (mode === 'preview' && !win) {
      setActionError(POPUP_BLOCKED_MESSAGE);
      return;
    }
    setPdfBusy(true);
    try {
      const res = await api.pressureTesting.certificates.pdf(cert.id);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' });
      if (win) showBlobInTab(win, blob);
      else saveBlobAs(blob, `${cert.certificate_number || `${cert.job.job_number}-DRAFT`}.pdf`);
    } catch (err) {
      win?.close();
      setActionError(await getBlobApiErrorMessage(err, 'The PDF could not be loaded.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const onRebuild = () => {
    if (!cert) return;
    setActionError(null);
    rebuild.mutate(
      { jobId: cert.job_id },
      {
        onSuccess: () => toast.success(`${cert.job.job_number} draft rebuilt`),
        onError: (err) => setActionError(ptWriteError(err, 'The draft was not rebuilt.').message),
      },
    );
  };

  const onIssued = (result: PtCertificate | null) => {
    const number = result?.certificate_number;
    toast.success(number ? `Issued ${number}` : 'Certificate issued');
    const email = result?.email ?? null;
    setEmailResult(email);
    if (email && !email.success) toast.error(`The certificate is issued, but the email to ${email.sent_to} failed.`);
  };

  const statusSkin = ptCertStatusSkin(status);

  const actions = cert ? (
    <div className="flex flex-wrap gap-2" data-testid="pt-cert-actions">
      {status === 'issued' ? (
        <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => openPdf('download')} disabled={pdfBusy || !cert.has_pdf} data-testid="pt-cert-download">
          {pdfBusy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Download className="size-4" aria-hidden="true" />}
          Download PDF
        </button>
      ) : (
        <>
          <button type="button" className={PT_BAND_SECONDARY} onClick={() => openPdf('preview')} disabled={pdfBusy} data-testid="pt-cert-preview">
            {pdfBusy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <FileText className="size-4" aria-hidden="true" />}
            Preview PDF
          </button>
          {canAct && (
            <button type="button" className={PT_BAND_SECONDARY} onClick={onRebuild} disabled={rebuild.isPending} data-testid="pt-cert-rebuild">
              {rebuild.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="size-4" aria-hidden="true" />}
              Rebuild draft
            </button>
          )}
          {canAct && status === 'draft' && (
            <>
              <button type="button" className={PT_BAND_SECONDARY} onClick={() => setDialog('reject')} data-testid="pt-cert-reject">
                <XCircle className="size-4" aria-hidden="true" />
                Reject
              </button>
              <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => setDialog('issue')} data-testid="pt-cert-issue">
                <PenLine className="size-4" aria-hidden="true" />
                Issue
              </button>
            </>
          )}
        </>
      )}
    </div>
  ) : undefined;

  return (
    <div data-testid="pt-cert-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Certificate review"
        title={cert ? cert.certificate_number || 'Draft' : query.isLoading ? 'Loading…' : 'Certificate'}
        onBack={back}
        backLabel="Back to certificates"
        actions={actions}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="pt-cert-detail">
          {!cert && query.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading the certificate" />
            </div>
          ) : notFound ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="pt-cert-not-found">
              This certificate was not found.
            </p>
          ) : !cert ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? undefined} subject="this certificate" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <PtSkinBadge skin={statusSkin} testId="pt-cert-status" value={status} />
                <PtSkinBadge skin={ptCertKindSkin(cert.kind)} testId="pt-cert-kind" value={cert.kind} />
                <Link to={`/pressure-testing/jobs/${cert.job_id}`} className="text-sm font-bold text-fieldiq-text underline">
                  {cert.job.job_number}
                </Link>
                <span className="text-sm text-fieldiq-muted">{cert.job.customer_name || '—'}</span>
              </div>
              {/* A failed BACKGROUND refetch keeps the certificate on screen and says so. */}
              {failure && (
                <p role="status" className="text-xs text-fieldiq-muted">
                  This certificate could not be refreshed just now, so it shows what loaded earlier.
                </p>
              )}
              {status === 'rejected' && (
                <div
                  role="status"
                  data-testid="pt-cert-rejected"
                  className="rounded-fi-panel border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text"
                >
                  <p className="font-bold">Rejected {formatInstant(cert.rejected_at, tz) ?? ''}</p>
                  <p className="mt-1 whitespace-pre-wrap">{cert.reject_reason || '—'}</p>
                  <p className="mt-2 text-xs">Rebuild the draft to issue it.</p>
                </div>
              )}
              {status === 'issued' && (
                <p
                  data-testid="pt-cert-issued-note"
                  className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text"
                >
                  Issued {formatInstant(cert.issued_at, tz) ?? '—'} by {cert.issued_signatory_name || '—'}. An issued certificate cannot be changed.
                </p>
              )}
              {actionError && (
                <p role="alert" data-testid="pt-cert-action-error" className="rounded-fi-panel border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text">
                  {actionError}
                </p>
              )}

              <CertificatePaper cert={cert} unit={unit} tz={tz} jobTests={jobTests} />

              <RecordSection title="AI remarks" icon={<Bot className="size-4 text-fieldiq-muted" aria-hidden="true" />} testId="pt-cert-ai-remarks">
                {cert.ai_remarks ? (
                  <>
                    <p className="whitespace-pre-wrap text-sm text-fieldiq-text">{cert.ai_remarks}</p>
                    <p className="mt-2 text-xs text-fieldiq-muted">
                      Drafted by AI{cert.ai_generated_at ? ` ${formatInstant(cert.ai_generated_at, tz) ?? ''}` : ''}. Read it before issuing; it does not decide the result.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-fieldiq-text">
                    {status === 'issued' ? 'No AI remarks were drafted for this certificate.' : 'No AI remarks yet. They are drafted after the certificate is built.'}
                  </p>
                )}
              </RecordSection>

              <RecordSection
                title={`Review flags (${cert.flags.length})`}
                icon={<AlertTriangle className="size-4 text-fieldiq-muted" aria-hidden="true" />}
                testId="pt-cert-review-flags"
              >
                <FlagList flags={cert.flags} />
              </RecordSection>

              <RecordSection title="Email" icon={<Mail className="size-4 text-fieldiq-muted" aria-hidden="true" />} testId="pt-cert-email">
                {emailResult ? (
                  <p className="text-sm text-fieldiq-text" data-testid="pt-cert-email-result" data-success={emailResult.success ? 'true' : 'false'}>
                    {emailResult.success
                      ? `Sent to ${emailResult.sent_to} when it was issued.`
                      : `Sending to ${emailResult.sent_to} failed: ${emailResult.error || 'no reason was given'}. The certificate is still issued.`}
                  </p>
                ) : status === 'issued' ? (
                  <p className="text-sm text-fieldiq-text">Earlier sends of this certificate are not listed here yet.</p>
                ) : (
                  <p className="text-sm text-fieldiq-text">
                    Not sent. It can be emailed to {cert.job.customer_contact_email || "the job's customer contact"} when it is issued.
                  </p>
                )}
              </RecordSection>
            </>
          )}
        </div>
      </section>

      {cert && dialog === 'reject' && <RejectDialog open onClose={() => setDialog(null)} cert={cert} />}
      {cert && dialog === 'issue' && <IssueDialog open onClose={() => setDialog(null)} cert={cert} onIssued={onIssued} />}
    </div>
  );
}
